import Check from '@mui/icons-material/Check';
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BulkActionsBarButton,
  BulkActionsBarText,
} from '~components/BulkActionsBar/BulkActionsBar';
import pushSettlementCreatedToast from '~components/Toasters/pushSettlementCreatedToast';
import { BillingLine } from '~hooks/useBillingLines/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { canReadDraftSettlementsOfParty } from '~hooks/useSettlements/draftSettlementsParty';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import {
  SettlementForm,
  SettlementFormHandler,
  useSettlementForm,
} from '~pages/Approvals/DriverPay/components/SettlementForm';
import { isJobsBillingLinesQueryKeyOfAnyJob } from '~pages/Approvals/Files/utils';
import { makeBillingLineGroupsWithJobsFilters } from '~queries/useBillingLineGroupsQuery';
import { makeGroupBillingLinesWithJobsFilters } from '~queries/useGroupBillingLinesQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import useSingleFlight from '~utils/hooks/useSingleFlight';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  categoryBySide,
  getBillingLinesParties,
  getSettlementsTabPath,
  sideLabelKeys,
  toSettlementLines,
} from '../chargeSelection';
import { ChargesSelectionActionsContext } from '../ChargesSelectionContext';
import { sumAllTotals } from '../groupBillingLines';
import AddToDraftSettlementDialog from './AddToDraftSettlementDialog';

const createSettlementLabelKeys = {
  [BillingLineSide.PAYABLE]:
    'approvals.approve_charges.selection.create_settlement.payable',
  [BillingLineSide.RECEIVABLE]:
    'approvals.approve_charges.selection.create_settlement.receivable',
} satisfies Record<BillingLineSide, string>;

const addToDraftLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.selection.add_to_draft.payable',
  [BillingLineSide.RECEIVABLE]:
    'approvals.approve_charges.selection.add_to_draft.receivable',
} satisfies Record<BillingLineSide, string>;

interface ChargesSideActionsProps {
  lines: BillingLine[];
  side: BillingLineSide;
}

export default function ChargesSideActions({ lines, side }: ChargesSideActionsProps) {
  const { uncheckLines } = useContext(ChargesSelectionActionsContext);
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currencyFormatter } = useCompanyCurrency();
  const { addLinesToSettlement, createSettlement } = useSettlements();
  const { dialogHandler } = useSettlementForm();
  const settlementFormRef = useRef<SettlementFormHandler>(null);
  const { isInFlight: isCreatingSettlement, runSingleFlight: runCreateSettlement } =
    useSingleFlight();
  const [isDraftPickerOpen, setIsDraftPickerOpen] = useState(false);
  const { isInFlight: isAddingToDraft, runSingleFlight: runAddToDraft } =
    useSingleFlight();

  const category = categoryBySide[side];
  const settlementLines = useMemo(() => toSettlementLines(lines), [lines]);
  const parties = useMemo(() => getBillingLinesParties(lines), [lines]);
  const linesBelongToSeveralParties = parties.length > 1;
  const canListPartyDrafts =
    parties.length === 1 && canReadDraftSettlementsOfParty(parties[0], side);
  const totalAmount = useMemo(() => sumAllTotals(lines).toNumber(), [lines]);
  const settledJobIds = useMemo(
    () => new Set(lines.flatMap(({ scope }) => (scope.job ? [scope.job.id] : []))),
    [lines],
  );

  const refreshBillingLines = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            isJobsBillingLinesQueryKeyOfAnyJob(queryKey, settledJobIds),
        }),
        queryClient.invalidateQueries(
          makeBillingLineGroupsWithJobsFilters([...settledJobIds]),
        ),
        queryClient.invalidateQueries(
          makeGroupBillingLinesWithJobsFilters([...settledJobIds]),
        ),
      ]),
    [queryClient, settledJobIds],
  );

  const handleCreateSettlementClick = useCallback(() => {
    dialogHandler.open();
  }, [dialogHandler]);

  const handleSettlementFormClose = useCallback(() => {
    if (isCreatingSettlement) {
      return;
    }

    dialogHandler.close();
  }, [dialogHandler, isCreatingSettlement]);

  const viewSettlement = useCallback(
    (settlementName: string) => {
      navigate(getSettlementsTabPath(category, settlementName));
    },
    [category, navigate],
  );

  const handleSettlementFormSubmit = useCallback(() => {
    settlementFormRef.current?.onSubmit?.((data) =>
      runCreateSettlement(async () => {
        try {
          const settlement = await createSettlement(companyId, {
            ...data,
            lines: settlementLines,
          });
          const settlementName = getFriendlySettlementId(settlement, category);
          pushSettlementCreatedToast({
            onViewSettlement: () => viewSettlement(settlementName),
            settlementName,
            toasterStore,
          });
          dialogHandler.close();
          uncheckLines(lines);
        } catch {
          // The hook has already reported the failure.
        } finally {
          await refreshBillingLines();
        }
      }),
    );
  }, [
    category,
    companyId,
    createSettlement,
    dialogHandler,
    lines,
    refreshBillingLines,
    runCreateSettlement,
    settlementLines,
    toasterStore,
    uncheckLines,
    viewSettlement,
  ]);

  const handleAddToDraftConfirm = useCallback(
    async (draftSettlement: Settlement) => {
      const draftSettlementId = draftSettlement.id;

      if (!draftSettlementId) {
        return;
      }

      await runAddToDraft(async () => {
        try {
          await addLinesToSettlement(companyId, draftSettlementId, settlementLines);
          toasterStore.push(
            alert(
              t('approvals.approve_charges.add_to_draft.lines_added', {
                count: settlementLines.length,
                name: getFriendlySettlementId(draftSettlement, category),
              }),
              AlertTypes.success,
            ),
          );
          setIsDraftPickerOpen(false);
          uncheckLines(lines);
        } catch {
          // The hook has already reported the failure.
        } finally {
          await refreshBillingLines();
        }
      });
    },
    [
      addLinesToSettlement,
      category,
      companyId,
      lines,
      refreshBillingLines,
      runAddToDraft,
      settlementLines,
      toasterStore,
      uncheckLines,
    ],
  );

  return (
    <>
      <BulkActionsBarText isStrong>
        {linesBelongToSeveralParties
          ? t(sideLabelKeys[side])
          : `${t(sideLabelKeys[side])} ${currencyFormatter(totalAmount)}`}
      </BulkActionsBarText>

      {linesBelongToSeveralParties ? (
        <Tooltip
          title={t('approvals.approve_charges.selection.multiple_parties', {
            count: parties.length,
            side: t(sideLabelKeys[side]),
          })}
        >
          <span>
            <BulkActionsBarButton color="success" disabled startIcon={<Check />}>
              {t(createSettlementLabelKeys[side])}
            </BulkActionsBarButton>
          </span>
        </Tooltip>
      ) : (
        <>
          <BulkActionsBarButton
            color="success"
            onClick={handleCreateSettlementClick}
            startIcon={<Check />}
          >
            {t(createSettlementLabelKeys[side])}
          </BulkActionsBarButton>

          {canListPartyDrafts && (
            <BulkActionsBarButton
              color="inherit"
              onClick={() => setIsDraftPickerOpen(true)}
              startIcon={<PlaylistAddOutlined />}
            >
              {t(addToDraftLabelKeys[side])}
            </BulkActionsBarButton>
          )}

          <SettlementForm
            ref={settlementFormRef}
            isBusy={isCreatingSettlement}
            isOpen={dialogHandler.isOpen}
            lines={settlementLines}
            onClose={handleSettlementFormClose}
            onSubmit={handleSettlementFormSubmit}
          />

          {canListPartyDrafts && (
            <AddToDraftSettlementDialog
              isBusy={isAddingToDraft}
              isOpen={isDraftPickerOpen}
              onClose={() => setIsDraftPickerOpen(false)}
              onConfirm={handleAddToDraftConfirm}
              party={parties[0]}
              side={side}
            />
          )}
        </>
      )}
    </>
  );
}
